import { CustomerMedia } from './appointmentProposalFiles.models';

export interface AppointmentProposalFilesState {
  ids: string[];
  entities: Record<string, CustomerMedia>;
  currentAppointmentId: string | null;
  error: string | null;
  isLoaded: boolean;
  isLoading: boolean;
  isProcessing: string[];
  failedUploads: string[];
  uploads: Record<string, number>;
}

type Action =
  | {
      type: 'FETCH_FILES';
      payload: { appointmentId: string };
    }
  | {
      type: 'FETCH_FILES_SUCCESS';
      payload: { files: CustomerMedia[] };
    }
  | { type: 'FETCH_FILES_FAILURE'; payload: { error: string } }
  | { type: 'UPLOAD_FILE'; payload: { file: CustomerMedia } }
  | {
      type: 'UPLOAD_FILE_PROGRESS';
      payload: { fileId: string; progress: number };
    }
  | { type: 'UPLOAD_FILE_CANCEL'; payload: { fileId: string } }
  | { type: 'UPLOAD_FILE_ERROR'; payload: { fileId: string } }
  | { type: 'UPLOAD_FILE_SUCCESS'; payload: { fileId: string } }
  | { type: 'UPLOAD_FILE_FAILURE'; payload: { error: string } }
  | { type: 'UPDATE_FILE'; payload: { fileId: string } }
  | { type: 'UPDATE_FILE_SUCCESS'; payload: { file: CustomerMedia } }
  | { type: 'UPDATE_FILE_FAILURE'; payload: { error: string } }
  | { type: 'DELETE_FILE'; payload: { fileId: string } }
  | { type: 'DELETE_FILE_SUCCESS'; payload: { fileId: string } }
  | { type: 'DELETE_FILE_FAILURE'; payload: { error: string } }
  | {
      type: 'GENERATE_PROPOSAL_OPTION_WORK_ORDER';
    }
  | {
      type: 'GENERATE_PROPOSAL_OPTION_WORK_ORDER_SUCCESS';
      payload: { files: CustomerMedia[] };
    }
  | {
      type: 'GENERATE_PROPOSAL_OPTION_WORK_ORDER_FAILURE';
      payload: { error: string };
    }
  | {
      type: 'SET_LOADING_STATE';
      payload: { isLoading: boolean; isLoaded: boolean };
    };

export const initialState: AppointmentProposalFilesState = {
  ids: [],
  entities: {},
  currentAppointmentId: null,
  isLoading: true,
  isLoaded: false,
  isProcessing: [],
  failedUploads: [],
  uploads: {},
  error: null,
};

export const proposalBuilderSurveyOptionsReducer = (
  state: AppointmentProposalFilesState,
  action: Action
): AppointmentProposalFilesState => {
  switch (action.type) {
    case 'FETCH_FILES':
      return {
        ...state,
        isLoading: true,
        isLoaded: !!state.ids.length,
        error: null,
        currentAppointmentId: action.payload.appointmentId,
      };
    case 'FETCH_FILES_SUCCESS':
      return {
        ...state,
        isLoaded: true,
        isLoading: false,
        ids: action.payload.files.map((a) => a.customerFileId),
        entities: action.payload.files.reduce(
          (prev, curr) => ({ ...prev, [curr.customerFileId]: curr }),
          {}
        ),
      };

    case 'UPLOAD_FILE': {
      return {
        ...state,
        isProcessing: [
          ...new Set(
            state.isProcessing.concat(action.payload.file.customerFileId)
          ),
        ],
        uploads: { ...state.uploads, [action.payload.file.customerFileId]: 0 },
        ids: [...new Set([...state.ids, action.payload.file.customerFileId])],
        entities: {
          ...state.entities,
          [action.payload.file.customerFileId]: action.payload.file,
        },
      };
    }

    case 'UPLOAD_FILE_PROGRESS':
      return {
        ...state,
        uploads: {
          ...state.uploads,
          [action.payload.fileId]: action.payload.progress,
        },
      };

    case 'UPLOAD_FILE_ERROR': {
      return {
        ...state,
        isProcessing: state.isProcessing.filter(
          (id) => id !== action.payload.fileId
        ),
        failedUploads: [
          ...new Set([...state.failedUploads, action.payload.fileId]),
        ],
      };
    }

    case 'UPLOAD_FILE_SUCCESS': {
      return {
        ...state,
        uploads: { ...state.uploads, [action.payload.fileId]: 100 },
        isProcessing: state.isProcessing.filter(
          (filename) => filename !== action.payload.fileId
        ),
        failedUploads: state.failedUploads.filter(
          (id) => id !== action.payload.fileId
        ),
      };
    }

    case 'UPDATE_FILE':
    case 'DELETE_FILE':
      return {
        ...state,
        isProcessing: [
          ...new Set([...state.isProcessing, action.payload.fileId]),
        ],
        error: null,
      };

    case 'UPDATE_FILE_SUCCESS':
      return {
        ...state,
        isProcessing: state.isProcessing.filter(
          (filename) => filename !== action.payload.file.customerFileId
        ),
        entities: {
          ...state.entities,
          [action.payload.file.customerFileId]: {
            ...action.payload.file,
            url: state.entities[action.payload.file.customerFileId].url,
          },
        },
      };

    case 'UPLOAD_FILE_CANCEL':
    case 'DELETE_FILE_SUCCESS':
      return {
        ...state,
        uploads: { ...state.uploads, [action.payload.fileId]: 100 },
        ids: state.ids.filter((id) => id !== action.payload.fileId),
        isProcessing: state.isProcessing.filter(
          (filename) => filename !== action.payload.fileId
        ),
      };

    case 'GENERATE_PROPOSAL_OPTION_WORK_ORDER':
      return {
        ...state,
        isLoading: true,
        error: null,
      };

    case 'GENERATE_PROPOSAL_OPTION_WORK_ORDER_SUCCESS':
      return {
        ...state,
        isLoading: false,
        isLoaded: true,
        ids: [
          ...new Set([
            ...state.ids,
            ...action.payload.files.map((f) => f.customerFileId),
          ]),
        ],
        entities: action.payload.files.reduce(
          (prev, curr) => ({ ...prev, [curr.customerFileId]: curr }),
          state.entities
        ),
      };

    case 'FETCH_FILES_FAILURE':
    case 'UPLOAD_FILE_FAILURE':
    case 'UPDATE_FILE_FAILURE':
    case 'DELETE_FILE_FAILURE':
    case 'GENERATE_PROPOSAL_OPTION_WORK_ORDER_FAILURE':
      return {
        ...state,
        isLoading: false,
        isLoaded: true,
        error: action.payload.error,
      };

    case 'SET_LOADING_STATE':
      return { ...state, ...action.payload };

    default:
      return state;
  }
};
